import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, Typography, TableRow, CardContent } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import {
  LOAD_INSURANCE_EQUIPMENTS,
  removeEquipmentFromInsuranceAction,
  loadInsuranceEquipments
} from 'src/store/actions/insuranceActions';
import {
  selectInsuranceEquipments,
  selectInsuranceEquipmentCount,
  selectInsuranceEquipmentQueryOptions
} from 'src/store/selectors/insuranceSelector';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import OverflowTipography from 'src/components/OverflowTipography';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import { CommonActions } from 'src/utils/constants/clients';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import Table from 'src/components/Table';
import TableCell from 'src/components/Table/TableCell';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import StatusIcon from 'src/components/StatusIcon';
import { IconStatusAndColor } from 'src/utils/constants/common';
import { ContractValidityStatus } from 'src/utils/constants/equipment';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import Tooltip from 'src/components/Tooltip';

const EquipmentsView = ({ insurance }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const getContractValidityStatusIcon = useCallback((equipment) => {
    if (!equipment.contractValidityStatus) {
      return (
        <StatusIcon
          description="EQUIPMENT_VIEW.CONTRACT_VALIDITY_STATUS.NO_DATA"
          type={IconStatusAndColor.NONE.status}
        />
      );
    }

    const tooltipText = equipment.contractEndDate 
      ? `${t('EQUIPMENT_VIEW.CONTRACT_VALIDITY_STATUS.CONTRACT_EXPIRES')} ${momentLocalTimezone(equipment.contractEndDate).format('DD/MM/YYYY')}`
      : t('EQUIPMENT_VIEW.CONTRACT_VALIDITY_STATUS.NO_END_DATE');

    switch (equipment.contractValidityStatus) {
      case ContractValidityStatus.VALID:
        return (
          <Tooltip>
            <StatusIcon
              description={tooltipText}
              type={IconStatusAndColor.OK.status}
            />
          </Tooltip>
        );
      case ContractValidityStatus.INVALID:
        return (
          <Tooltip>
            <StatusIcon
              description={tooltipText}
              type={IconStatusAndColor.ERROR.status}
            />
          </Tooltip>
        );
      case ContractValidityStatus.NO_CONTRACT:
        return (
          <StatusIcon
            description="EQUIPMENT_VIEW.CONTRACT_VALIDITY_STATUS.NO_CONTRACT"
            type={IconStatusAndColor.NONE.status}
          />
        );
      default:
        return (
          <StatusIcon
            description="EQUIPMENT_VIEW.CONTRACT_VALIDITY_STATUS.NO_DATA"
            type={IconStatusAndColor.NONE.status}
          />
        );
    }
  }, [t]);
  const equipments = useSelector(selectInsuranceEquipments);
  const equipmentCount = useSelector(selectInsuranceEquipmentCount);
  const { page } = useSelector(selectInsuranceEquipmentQueryOptions);
  const loading = useSelector(
    isLoadingSelector([LOAD_INSURANCE_EQUIPMENTS.action])
  );

  const [openRemoveEquipment, setOpenRemoveEquipment] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState(null);

  const handleChangePage = useCallback(
    (event, value) => {
      dispatch(
        loadInsuranceEquipments({
          policyId: insurance.policyId,
          page: value
        })
      );
    },
    [dispatch, insurance.policyId]
  );

  const getInsuranceMenuOptions = useCallback(() => {
    return [
      {
        name: CommonActions.REMOVE_EQUIPMENT,
        label: `ENUMS.ADMINISTRATIVE_ACTIONS.${CommonActions.REMOVE_EQUIPMENT}`,
        icon: <DeleteIcon />
      }
    ];
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setOpenRemoveEquipment(false);
    setEquipmentToDelete(null);
  }, []);

  const handleClickMore = useCallback((option, equipment) => {
    if (option === CommonActions.REMOVE_EQUIPMENT) {
      setEquipmentToDelete(equipment);
      setOpenRemoveEquipment(true);
    }
  }, []);

  const handleDeleteModal = useCallback(() => {
    if (equipmentToDelete) {
      dispatch(
        removeEquipmentFromInsuranceAction(
          insurance.policyId,
          [equipmentToDelete.id],
          handleCloseDeleteModal
        )
      );
      handleCloseDeleteModal();
    }
  }, [dispatch, insurance.policyId, equipmentToDelete]);

  const theads = useMemo(
    () => () => (
      <TableRow component="tr">
        <TableCell variant="head" style={{ width: '10%' }}>
          {t('EQUIPMENT_VIEW.EQUIPMENT_ID')}
        </TableCell>
        <TableCell variant="head" style={{ width: '15%' }}>
          {t('EQUIPMENT_VIEW.SERIAL_NUMBER')}
        </TableCell>
        <TableCell variant="head" style={{ width: '20%' }}>
          {t('EQUIPMENT_VIEW.CLIENT')}
        </TableCell>
        <TableCell variant="head" style={{ width: '20%' }}>
          {t('EQUIPMENT_VIEW.DESCRIPTION')}
        </TableCell>
        <TableCell variant="head" style={{ width: '10%' }}>
          {t('EQUIPMENT_VIEW.PRICE')}
        </TableCell>
        <TableCell variant="head" style={{ width: '10%' }}>
          {t('EQUIPMENT_VIEW.YEAR')}
        </TableCell>
        <TableCell variant="head" style={{ width: '15%' }}>
          {t('EQUIPMENT_VIEW.CONTRACT_VALIDITY')}
        </TableCell>
        <TableCell variant="head" style={{ width: '5%' }}>
          {t('EQUIPMENT_VIEW.ACTIONS')}
        </TableCell>
      </TableRow>
    ),
    [t]
  );

  const tbody = useMemo(
    () => () => (
      <>
        {equipments.map(equipment => (
          <TableRow key={equipment.id}>
            <TableCell>
              <Link to={`/inventory/${equipment.id}`}>{equipment.id}</Link>
            </TableCell>
            <TableCell>
              {equipment.serialNumber || t('COMMON.NO_DATA')}
            </TableCell>
            <TableCell>
              <OverflowTipography title={equipment.client} maxWidth="200px">
                {equipment.client ? (
                  <Link to={`/clients/detail/${equipment.clientId}`}>
                    {equipment.client}
                  </Link>
                ) : (
                  t('COMMON.NO_DATA')
                )}
              </OverflowTipography>
            </TableCell>
            <TableCell>
              {`${equipment.brand} ${equipment.model}` || t('COMMON.NO_DATA')}
            </TableCell>
            <TableCell>
              {equipment.price
                ? formatAmountWithDecimals(equipment.price)
                : t('COMMON.NO_DATA')}
            </TableCell>
            <TableCell>{equipment.year || t('COMMON.NO_DATA')}</TableCell>
            <TableCell>
              {getContractValidityStatusIcon(equipment)}
            </TableCell>
            <TableCell>
              <MoreMenu
                options={getInsuranceMenuOptions()}
                handleClickOption={option => handleClickMore(option, equipment)}
              />
            </TableCell>
          </TableRow>
        ))}
      </>
    ),
    [equipments, t, getInsuranceMenuOptions, handleClickMore, getContractValidityStatusIcon]
  );

  const pagination = useMemo(
    () => () => {
      const currentPage = Number(page) || 0;
      const tableSize = Number(process.env.REACT_APP_TABLE_SIZE) || 10;

      return (
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={equipmentCount || 0}
          rowsPerPage={tableSize}
          page={currentPage}
          onPageChange={handleChangePage}
          ActionsComponent={TablePaginationCustom}
          labelDisplayedRows={({ from, to, count: c }) => {
            return `${from}-${to} ${t('COMMON.OF')} ${c}`;
          }}
        />
      );
    },
    [equipmentCount, page, handleChangePage, t]
  );

  if (!insurance && !loading) {
    return (
      <Box mt={3}>
        <Typography variant="h6" color="textSecondary">
          {t('COMMON.NO_INFORMATION')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box mt={6}>
      <Card>
        <CardContent style={{ padding: 0, marginBottom: '16px' }}>
          {(() => {
            if (!loading && (!equipments || equipments.length === 0)) {
              return (
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ mt: 2, padding: 2 }}
                >
                  {t('INSURANCE_VIEW.NO_EQUIPMENTS_ASSOCIATED')}
                </Typography>
              );
            }

            return (
              <>
                <Table
                  theads={theads}
                  tbody={tbody}
                  loading={loading}
                  pagination={pagination}
                />
                <ConfirmationDialog
                  open={openRemoveEquipment}
                  handleClose={handleCloseDeleteModal}
                  title={t('INSURANCE_VIEW.REMOVE_EQUIPMENT')}
                  description={t('INSURANCE_VIEW.REMOVE_EQUIPMENT_DISCLAIMER')}
                  onSubmit={handleDeleteModal}
                />
              </>
            );
          })()}
        </CardContent>
      </Card>
    </Box>
  );
};

EquipmentsView.propTypes = {
  insurance: PropTypes.object
};

export default EquipmentsView;
